import { useCallback, useEffect, useRef, useState } from 'react'
import type { AsyncState } from './useAsync'

/** Like useAsync, but re-runs on an interval and keeps the previous result
 *  across a refresh.
 *
 *  useAsync clears `data` the moment a new call begins, deliberately — it is
 *  what stops one station's chart being drawn under another station's
 *  heading. That is right for navigation and wrong for a refresh: a panel
 *  that blanked every half minute would be unreadable, and it would blank
 *  precisely *because* the data is fresh.
 *
 *  So the two cases are separated. A dependency change clears, because the
 *  question changed and the old answer belongs to a different station. A tick
 *  does not, because it is the same question asked again.
 *
 *  Polling stops while the tab is hidden and refreshes immediately when it
 *  comes back. A background tab polling on behalf of a reader who is not
 *  looking is pure cost.
 */
export function usePolled<T>(fn: () => Promise<T>, deps: unknown[],
                             intervalMs: number): AsyncState<T> {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const generation = useRef(0)
  const current = useRef(fn)
  current.current = fn

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  // A new question: clear, the way useAsync does.
  useEffect(() => {
    setData(undefined)
    setError(undefined)
    setLoading(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    let cancelled = false

    const run = () => {
      const mine = ++generation.current
      current.current().then(
        (result) => {
          if (cancelled || mine !== generation.current) return
          setData(result)
          setError(undefined)
          setLoading(false)
        },
        (err: unknown) => {
          if (cancelled || mine !== generation.current) return
          // A failed refresh keeps the last good readings on screen rather
          // than replacing them with an error. The "as of" line already says
          // how old they are, which is the honest signal either way.
          setError(err instanceof Error ? err.message : String(err))
          setLoading(false)
        },
      )
    }

    run()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') run()
    }, intervalMs)
    const onVisible = () => {
      if (document.visibilityState === 'visible') run()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, intervalMs])

  return { data, error, loading, reload }
}
